
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useNavigate } from "react-router-dom";
import Hey from "@/lib/hey";
import { useAnalytics } from "@/hooks/use-analytics";

const DeleteAccount = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);
  const { trackProfileAction, trackError } = useAnalytics();

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      trackProfileAction('delete_account', 'success', 'initiated');
      console.debug("🗑️ Starting account deletion process");
      
      const isDeleted = await Hey.HeyUtils.getInstance().deleteAccount();
      
      if (isDeleted) {
        trackProfileAction('delete_account', 'success', 'completed');
        toast({
          title: t("common.success"),
          description: t("settings.accountDeleted")
        });

        // Clear localStorage and redirect to auth page
        localStorage.clear();
        setTimeout(() => {
          navigate("/auth");
        }, 1500);
      }
    } catch (error) {
      console.error("🚨 ERROR: Error deleting account:", error);
      trackProfileAction('delete_account', 'failed', error.message);
      trackError('auth', `Account deletion failed: ${error.message}`, 'DeleteAccount');
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: t("settings.deleteError") + ": " + error.message
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-medium">{t("settings.deleteAccount")}</h3>
        <p className="text-sm text-muted-foreground">
          {t("settings.deleteAccountDescription")}
        </p>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="outline" 
              disabled={isDeleting} 
              onClick={() => trackProfileAction('delete_account', 'success', 'dialog_opened')} 
              className="text-white hover:text-white bg-[#ff0000]"
            >
              {isDeleting ? t("common.deleting") : t("settings.deleteAccount")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("settings.confirmDelete")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("settings.deleteWarning")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => trackProfileAction('delete_account', 'success', 'dialog_cancelled')}>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t("settings.confirmDeleteButton")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default DeleteAccount;
